import get from 'lodash/get';

import { countryCode } from 'Helpers/Geo/Country';
import { post } from 'Api';
import { setCheckoutMessage, resetCheckoutMessage, LOCATION_CHANGED } from 'Redux/ducks/checkout-message';
import { reloadForLocationChange, clearLocationReload } from 'Helpers/Geo/location-drift';
import { setVoucherMessage, resetVoucherMessage } from 'Redux/ducks/voucher-message';
import store, { history } from 'Redux/store';
import State from 'Helpers/State';
import CheckoutUtils from 'Helpers/Checkout/CheckoutUtils';
import { orderCalcCheck } from 'Helpers/Checkout/order-calc-checkout';

const CREATE_ORDER = 'frontend/checkout/CREATE_ORDER';
const RESET_CHECKOUT = 'frontend/checkout/RESET_CHECKOUT';

const initialStateCheckout = {
  order: null,
  userId: undefined,
  pageId: undefined,
  countryCode: undefined,
  payment: null,
};

export function resetCheckout() {
  return store.dispatch({ type: RESET_CHECKOUT, payload: null });
}

/**
 * @param {Object} body      The create-order request body.
 * @param {Object} orderCalc cart.orderCalc, when the cart came from holocron
 *                           order-calc. The New Relic createOrder event gets
 *                           its check against the order (orderCalcCheck).
 */
export function createOrder(body, orderCalc) {
  resetCheckoutMessage();
  resetVoucherMessage();
  return async (dispatch) => {
    const { response, data } = await post(dispatch, '/user/payment/create-order', body);


    if (response.ok) {
      const order = get(data, 'order', initialStateCheckout.order);
      const userId = State.getUserId();
      const pageId = State.getPageId();
      const items = get(order, 'items');
      const completeOrderTransaction = CheckoutUtils.getCompleteOrderTransaction(items);
      const authState = store.getState().auth;
      const roktOptOut = get(authState, 'rokt_opt_out', false);
      const discounts = get(order, 'discounts', []);
      const payment = get(data, 'paymentData', initialStateCheckout.payment);

      const payload = {
        order,
        hitType: 'CreateOrder',
        transactionId: get(order, '_id'),
        paymentMethod: get(order, 'paymentMethod', ''),
        transactionTotal: get(order, 'report.total', ''),
        transactionTax: get(completeOrderTransaction, 'transactionTax', ''),
        transactionProducts: get(completeOrderTransaction, 'transactionProducts', ''),
        pageId,
        discountPercent: get(order, 'discounts[0].percent', 0),
        newCustomer: !!data.newCustomer,
        roktOptOut,
        userId,
        countryCode,
        payment,
        // order-calc Stage 6.4: for the New Relic createOrder event only. The
        // other maps and the reducer read named fields, so they do not get it.
        orderCalcCheck: orderCalcCheck(orderCalc, order),
      };

      if (discounts.length > 0) {
        discounts.forEach((discount) => {
          if (discount.track_to_tapfiliate) {
            payload.track_to_tapfiliate = discount.code;
          }
        });
      }

      const siteBrand = get(order, 'siteBrand');

      if (siteBrand) {
        payload.siteBrand = siteBrand;
      }

      dispatch({ type: CREATE_ORDER, payload });
      clearLocationReload();
      return data;
    }

    // Amen priced the order in another location than the cart (the IP changed after the page
    // loaded). Reload once to take the new window.countryData. When the reload is refused (one
    // already happened), fall through: the checkout message tells the customer to reload.
    if (get(data, 'status') === LOCATION_CHANGED && reloadForLocationChange()) {
      return {};
    }

    const type = get(data, 'type', '');
    if (type === 'billing') {
      return data;
    }
    if (type === 'checkout') {
      setCheckoutMessage(data);
    }
    if (type === 'voucher') {
      setVoucherMessage(data);
    }
    resetCheckout();
    return {};
  };
}

export function giveawayOrder(orderId) {
  return async (dispatch) => {
    try {
      const { response, data } = await post(dispatch, '/user/payment/giveaway-order', { orderId });
      if (!response.ok) {
        throw new Error(response.status);
      }
      let authResponse = get(data, 'authResponse', '');
      authResponse = authResponse.toUpperCase();
      const merchantReference = get(data, 'merchantReference', '');
      const pspReference = get(data, 'pspReference', '');
      const url = `/receipt?utm_nooverride=1&authResult=${authResponse}&merchantReference=${merchantReference}&merchantReturnData=false&paymentMethod=false&pspReference=${pspReference}&shopperLocale=en_GB&skinCode=false`;
      history.push(url);
      return null;
    } catch (e) {
      setCheckoutMessage({
        data: {},
        status: 'GIVEAWAY_UNEXPECTED_ERROR',
        type: 'checkout',
        message: 'There was a problem claiming this promotion - please try again or contact support',
      });
      return resetCheckout();
    }
  };
}
